import { type Container, createIdentifier } from '@blocksuite/global/di';
import { Extension } from '@blocksuite/store';
import { type Signal, signal } from '@preact/signals-core';

export interface UploadProgress {
  /** 0..1 ratio */
  ratio: number;
  bytesUploaded: number;
  bytesTotal: number;
}

/**
 * Per-block upload progress tracking.
 *
 * Used by attachment/image blocks to show a progress bar
 * while a file is being uploaded in the background.
 */
export interface IUploadProgressService {
  /** Get or create a progress signal for a block. */
  get(blockId: string): Signal<UploadProgress | null>;
  /** Update progress for a block. */
  set(blockId: string, bytesUploaded: number, bytesTotal: number): void;
  /** Remove tracking for a block (upload finished or failed). */
  delete(blockId: string): void;
}

export const UploadProgressProvider = createIdentifier<IUploadProgressService>(
  'UploadProgressService'
);

export class UploadProgressService
  extends Extension
  implements IUploadProgressService
{
  private readonly _map = new Map<string, Signal<UploadProgress | null>>();

  get(blockId: string): Signal<UploadProgress | null> {
    let s = this._map.get(blockId);
    if (!s) {
      s = signal<UploadProgress | null>(null);
      this._map.set(blockId, s);
    }
    return s;
  }

  set(blockId: string, bytesUploaded: number, bytesTotal: number): void {
    const ratio = bytesTotal > 0 ? bytesUploaded / bytesTotal : 0;
    const s = this.get(blockId);
    s.value = { ratio, bytesUploaded, bytesTotal };
  }

  delete(blockId: string): void {
    const s = this._map.get(blockId);
    if (s) {
      s.value = null;
      this._map.delete(blockId);
    }
  }

  static override setup(di: Container) {
    di.addImpl(UploadProgressProvider, UploadProgressService);
  }
}
